import { useEffect, useState } from "react";
import { Formik } from "formik";
import { toast } from "sonner";
import * as Yup from "yup";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaEdit,
  FaRegCopy,
  FaTimes,
  FaPlus,
} from "react-icons/fa";
import ApiServices from "../../services/apiservices";
import { POST_STATUS } from "../../utils/constants";

const categories = [
  "Public Announcement",
  "Tender Notice",
  "Short Level Falls",
  "Map-Meter-Refund",
  "Publications",
];

const PostTable = () => {
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchPosts = async () => {
    try {
      const res = await ApiServices.getRequest(`/posts/fetch.php?page=${page}`);      
      setPosts(res.posts || []);
      setTotalPages(res.totalPages || 1);
    } catch (error) {
      toast.error("Failed to load posts");
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [page]);

  // Unified function to handle status changes
  const handleToggleStatus = async (id) => {
    if (!window.confirm("Change the visibility status of this post?")) return;
    
    try {
      const res = await ApiServices.deleteRequest(`/posts/togglestatus.php?id=${id}`);
      if (res.success) {
        toast.success(res.message);
        fetchPosts();
        setIsModalOpen(false); // Close modal if status is changed from within it
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update status");
    }
  };
  const handleAddToLatestUpdates = async (id) => {
    if (!window.confirm("KIndly Confirm Action?")) return;
    
    try {
      const res = await ApiServices.putRequest(`/posts/togglelatestnews.php?id=${id}`);
      if (res.success) {
        toast.success(res.message);
        fetchPosts();
        setIsModalOpen(false); // Close modal if status is changed from within it
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update status");
    }
  };

  const copyLink = (post) => {
    const url = `${window.location.origin}/${post.postLinkTitle}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard!");
  };

  const validationSchema = Yup.object({
    title: Yup.string().required("Title required"),
    category: Yup.string().required("Category required"),
    mainContent: Yup.string().when("category", {
      is: (val) => val !== "Tender Notice",
      then: (schema) => schema.required("Main content is required"),
    }),
  });

  const ImagePreview = ({ file, currentImageUrl }) => {
    const source = file instanceof File ? URL.createObjectURL(file) : currentImageUrl;
    if (!source) return <div className="w-32 h-32 mb-2 border rounded bg-gray-100 flex items-center justify-center text-xs text-gray-400">No Image</div>;

    return (
      <div className="relative w-32 h-32 mb-2 border rounded overflow-hidden bg-gray-50">
        <img src={source} className="w-full h-full object-cover" alt="Preview" />
      </div>
    );
  };

  const handleSubmit = async (values) => {    
    const formData = new FormData();
    Object.keys(values).forEach((key) => {
      const value = values[key];
      if (value === undefined || value === null) return;
      formData.append(key, value);
    });

    try {
      const res = await ApiServices.postRequest(`/posts/update.php?id=${selectedPost.id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.success) {
        toast.success(res.message);
        setIsModalOpen(false);
        fetchPosts();
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  return (
    <main className="flex-1 bg-white p-6 overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">Manage Posts</h2>
        <a href="/admin/post/new" className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded shadow-md transition-all">
          <FaPlus /> New Post
        </a>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full border text-sm text-left">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-3 py-2 text-center">Image</th>
              <th className="border px-3 py-2">Title</th>
              <th className="border px-3 py-2">Category</th>
              <th className="border px-3 py-2">Author</th>
              <th className="border px-3 py-2 text-center">Status</th>
              <th className="border px-3 py-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((post) => (
              <tr key={post.id} className="hover:bg-gray-50">
                <td className="border px-3 py-2 text-center">
                  {post.featuredImage ? (
                    <img src={post.featuredImage} className="w-12 h-12 mx-auto object-cover rounded" alt="" />
                  ) : "—"}
                </td>
                <td className="border px-3 py-2 font-medium">{post.title}</td>
                <td className="border px-3 py-2">{post.category}</td>
                <td className="border px-3 py-2">{post.author}</td>
                <td className="border px-3 py-2 text-center">
                  <button 
                    onClick={() => handleToggleStatus(post.id)}
                    className={`px-2 py-1 rounded text-xs font-semibold uppercase tracking-wider transition-all ${
                      post.status === POST_STATUS.PUBLISHED 
                      ? "bg-green-100 text-green-700 border border-green-200 hover:bg-green-200" 
                      : "bg-amber-100 text-amber-700 border border-amber-200 hover:bg-amber-200"
                    }`}
                  >
                    {post.status}
                  </button>
                </td>
                <td className="border px-3 py-2 text-center">
                  <div className="flex gap-4 justify-center">
                    <button title="Edit Post" onClick={() => { setSelectedPost(post); setIsModalOpen(true); }}>
                      <FaEdit className="text-blue-600 hover:text-blue-800 text-lg" />
                    </button>
                    <button title="Copy Link" onClick={() => copyLink(post)}>
                      <FaRegCopy className="text-gray-600 hover:text-gray-800 text-lg" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {isModalOpen && selectedPost && (
          <motion.div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col relative">
              <button onClick={() => setIsModalOpen(false)} className="absolute top-4 right-4 text-gray-500 hover:text-red-500 z-10">
                <FaTimes size={20} />
              </button>

              <div className="p-6 border-b">
                <h3 className="text-xl font-bold">Edit Post - {selectedPost.title}</h3>
              </div>

              <div className="p-6 overflow-y-auto">
                <Formik
                  enableReinitialize
                  initialValues={{
                    ...selectedPost,
                    introContent: selectedPost.introContent || "",
                    mainContent: selectedPost.mainContent || "",
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ values, errors, touched, setFieldValue, handleChange, handleSubmit }) => (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Post Title</label>
                          <input name="title" value={values.title} onChange={handleChange} className={`w-full border px-3 py-2 rounded outline-none ${errors.title && touched.title ? 'border-red-500' : 'border-gray-300'}`} />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Category</label>
                          <select name="category" value={values.category} onChange={handleChange} className="w-full border border-gray-300 px-3 py-2 rounded outline-none">
                            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                      </div>

                      {values.category !== "Tender Notice" && (
                        <>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Intro Content</label>
                            <textarea name="introContent" rows="2" value={values.introContent} onChange={handleChange} className="w-full border border-gray-300 px-3 py-2 rounded outline-none" />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Main Body Content</label>
                            <textarea name="mainContent" rows="5" value={values.mainContent} onChange={handleChange} className={`w-full border px-3 py-2 rounded outline-none ${errors.mainContent && touched.mainContent ? 'border-red-500' : 'border-gray-300'}`} />
                          </div>
                        </>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="flex flex-col">
                          <label className="text-sm font-semibold mb-2">Featured Image</label>
                          <ImagePreview file={values.featuredImage} currentImageUrl={selectedPost.featuredImage} />
                          <input type="file" className="text-xs" onChange={(e) => setFieldValue("featuredImage", e.currentTarget.files[0])} />
                        </div>
                        {values.category !== "Tender Notice" && (
                          <>
                            {/* <div className="flex flex-col">
                              <label className="text-sm font-semibold mb-2">Content Image 1</label>
                              <ImagePreview file={values.contentImage1} currentImageUrl={selectedPost.contentImage1} />
                              <input type="file" className="text-xs" onChange={(e) => setFieldValue("contentImage1", e.currentTarget.files[0])} />
                            </div> */}
                            {/* <div className="flex flex-col">
                              <label className="text-sm font-semibold mb-2">Content Image 2</label>
                              <ImagePreview file={values.contentImage2} currentImageUrl={selectedPost.contentImage2} />
                              <input type="file" className="text-xs" onChange={(e) => setFieldValue("contentImage2", e.currentTarget.files[0])} />
                            </div> */}
                          </>
                        )}
                      </div>

                      <div className="flex justify-between items-center pt-6 border-t mt-6">
                        <button 
                          type="button" 
                          onClick={() => handleToggleStatus(selectedPost.id)} 
                          className={`px-4 py-2 rounded border font-medium transition-all ${
                            values.status === POST_STATUS.PUBLISHED 
                            ? "bg-amber-50 text-amber-700 border-amber-600 hover:bg-amber-600 hover:text-white" 
                            : "bg-green-50 text-green-700 border-green-600 hover:bg-green-600 hover:text-white"
                          }`}
                        >
                          {values.status === POST_STATUS.PUBLISHED ? "Unpublish (Draft)" : "Publish Now"}
                        </button>

                        <button 
                          type="button" 
                          onClick={() => handleAddToLatestUpdates(selectedPost.id)} 
                          className={`px-4 py-2 rounded border font-medium transition-all ${
                            values.addtoLatestUpdate === true
                            ? "bg-amber-50 text-amber-700 border-amber-600 hover:bg-amber-600 hover:text-white" 
                            : "bg-green-50 text-green-700 border-green-600 hover:bg-green-600 hover:text-white"
                          }`}
                        >
                           {values.addtoLatestUpdate === true ? " Remove from latest News" : "Add to latest News"}
                        </button>
                        
                        <button type="submit" className="px-10 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded shadow-lg transition-all">
                          Update Post
                        </button>
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default PostTable;